import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Box } from "@mui/material";
import { FileUpload, Edit, PlaylistAdd } from "@mui/icons-material";
import Playlists from "./playlists";
import SectionNavBar from "../../components/SectionNavBar";
import useQueryParam from "../../hooks/useQueryParam";
import AddToPlaylist from "./AddToPlaylist";
import UploadVideoFilesComponent from "../../components/UploadVideoFilesComponent";
import { usePublisherContext } from "../../hooks/usePublisherProvider";
import MRSSFeeds from "./MRSSFeeds";
import VideoLibrary from "./VideoLibrary";
import AnchorMenu from "../../components/Menu/AnchorMenu";
import BulkEditMediaModal from "../../components/Modals/BulkEditMediaModal";
import { Video, Domain } from "../../api/types";
import { Query } from "../../hooks/useVideos";
import { BulkEditType } from "../../@types/media.types";
import { useUploadStateContext } from "../../context/upload/upload.provider";
import { isAddToPlaylistEnabled } from "../../constants/constants";
import { GenerateFormType } from "../../@types/playlist.types";
import { useDomains } from "../../hooks/useDomains";

export default function MediaGallery(
  {
    asAdmin,
    publisherGroup: groupName,
  }: { asAdmin?: boolean; publisherGroup: string } = {
    asAdmin: false,
    publisherGroup: "",
  },
) {
  const { publisherSlug, publisherGroup, publisherUsername } =
    usePublisherContext(asAdmin);
  const [isCreateFormVisible, setIsCreateFormVisible] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [anchorElMenu, setAnchorElMenu] = useState<HTMLButtonElement | null>(
    null,
  );
  const [anchorElDomain, setAnchorElDomain] =
    useState<HTMLButtonElement | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [isDomainMenuOpen, setIsDomainMenuOpen] = useState<boolean>(false);
  const tab = useQueryParam("tab", (t) => parseInt(t, 10) || 0);
  const [selectedTab, setSelectedTab] = useState(tab);
  const [selectedVideos, setSelectedVideos] = useState<Video[]>([]);
  const [bulkEditType, setBulkEditType] = useState<BulkEditType>(
    BulkEditType.TAGS,
  );
  const [selectedPlaylists, setSelectedPlaylists] = useState([]);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState<boolean>(false);
  const [generatePlaylistType, setGeneratePlaylistType] = useState<
    GenerateFormType | undefined
  >();
  const [isEditMenuOpen, setIsEditMenuOpen] = useState<boolean>(false);
  const [isPlaylistMenuOpen, setIsPlaylistMenuOpen] = useState<boolean>(false);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState<boolean>(false);
  const [videoQuery, setVideoQuery] = useState<Query>({});
  const { uploadInProgress } = useUploadStateContext();
  const query = asAdmin && { slug: publisherSlug };
  const { domains } = useDomains({
    enabled: true,
    query,
  });

  const handleUploadClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (domains && domains.length > 0) {
      setAnchorElDomain(e.currentTarget);
      setIsDomainMenuOpen(true);
    } else {
      setSelectedDomain(null);
      setShowUploadPopup(true);
    }
  };

  const handleDomainSelect = (domain: Domain) => {
    setSelectedDomain(domain);
    setIsDomainMenuOpen(false);
    setShowUploadPopup(true);
  };

  const domainMenuItems =
    domains?.map((domain) => ({
      label: domain.domain,
      onClick: () => handleDomainSelect(domain),
    })) || [];

  const createOptions = {
    isVisible: isCreateFormVisible,
    onClose: () => setIsCreateFormVisible(false),
    onSubmit: () => {
      setIsCreateFormVisible(false);
    },
  };
  const tabs = [
    { label: "Video Library" },
    { label: "Playlists" },
    { label: "MRSS Feed" },
  ];
  const history = useHistory();
  const buttonsConfig = {
    0: [
      {
        id: "upload-videos",
        key: "upload-videos",
        text: uploadInProgress ? "Uploading..." : "Upload Videos",
        startIcon: <FileUpload />,
        isEnabled: !uploadInProgress,
        onClick: handleUploadClick,
      },
      {
        id: "edit-videos",
        key: "edit-videos",
        text: "Edit",
        startIcon: (
          <Edit
            sx={{ color: selectedVideos.length > 0 ? "#ffffff" : "#d1d9de" }}
          />
        ),
        isEnabled: selectedVideos.length > 0,
        onClick: (e) => {
          setAnchorElMenu(e.currentTarget);
          setIsEditMenuOpen(true);
        },
      },
      {
        id: "add-to-playlist",
        key: "add-to-playlist",
        text: "Add to Playlist",
        isEnabled:
          (isAddToPlaylistEnabled || asAdmin) && selectedVideos.length > 0,
        onClick: (e) => {
          setAnchorEl(e.currentTarget);
        },
      },
    ],
    1: [
      {
        id: "generate-playlist",
        key: "generate-playlist",
        text: "Generate Playlist",
        startIcon: <PlaylistAdd sx={{ marginRight: "4px" }} />,
        onClick: (e) => {
          setAnchorElMenu(e.currentTarget);
          setIsPlaylistMenuOpen(true);
        },
        isEnabled: !isCreateFormVisible,
      },
    ],
    2: [
      {
        id: "edit-mrss",
        key: "edit-mrss",
        text: "Edit",
        startIcon: <Edit />,
        isEnabled: false,
        onClick: (e) => {
          setAnchorElMenu(e.currentTarget);
        },
      },
    ],
  };

  const editMenuItems = [
    {
      label: "Categories",
      onClick: () => {
        setBulkEditType(BulkEditType.CATEGORY);
        setIsBulkEditOpen(true);
      },
    },
    {
      label: "Tags",
      onClick: () => {
        setBulkEditType(BulkEditType.TAGS);
        setIsBulkEditOpen(true);
      },
    },
    {
      label: "Delete Selected",
      onClick: () => setIsBulkDeleteOpen(true),
    },
  ];

  const generatePlaylistMenu = [
    {
      label: "Generate by categories",
      // TODO: Add function for edit by categories
      onClick: () => setGeneratePlaylistType("category"),
    },
    {
      label: "Generate by tags",
      // TODO: Add function for edit by tags
      onClick: () => setGeneratePlaylistType("tag"),
    },
    {
      label: "Custom Playlist",
      onClick: () => setGeneratePlaylistType("custom"),
    },
  ];

  const handleSelectedTab = (tabValue) => {
    if (isCreateFormVisible) setIsCreateFormVisible(false);
    setSelectedTab(tabValue);
    history.push(`${window.location.pathname}?tab=${tabValue}`);
  };

  const renderTabContent = () => {
    switch (tab) {
      case 0:
        return (
          <VideoLibrary
            asAdmin={asAdmin}
            publisherSlug={publisherSlug}
            createOptions={createOptions}
            selectedVideos={selectedVideos}
            onChangeSelected={setSelectedVideos}
            isBulkDeleteOpen={isBulkDeleteOpen}
            setIsBulkDeleteOpen={setIsBulkDeleteOpen}
            query={videoQuery}
            setQuery={setVideoQuery}
          />
        );
      case 1:
        return (
          <Playlists
            asAdmin={asAdmin}
            publisherSlug={publisherSlug}
            publisherGroup={groupName || publisherGroup}
            publisherUsername={publisherUsername}
            createOptions={createOptions}
            selectedPlaylists={selectedPlaylists}
            onChangeSelected={setSelectedPlaylists}
            generateFormType={generatePlaylistType}
            closeGenerateForm={() => setGeneratePlaylistType(undefined)}
          />
        );
      case 2:
        return (
          <MRSSFeeds
            asAdmin={asAdmin}
            publisherSlug={publisherSlug}
            createOptions={createOptions}
            selected={selectedVideos}
            onChangeSelected={setSelectedVideos}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (showUploadPopup) setShowUploadPopup(false);
  }, [showUploadPopup]);

  return (
    <Box padding="20px">
      <Box display="flex" flexDirection="column" justifyContent="center">
        <Box sx={{ fontSize: "24px", marginY: "12px" }}>Media Gallery</Box>

        {/* Domain selection menu */}
        <AnchorMenu
          anchorEl={anchorElDomain}
          open={isDomainMenuOpen}
          onClose={() => setIsDomainMenuOpen(false)}
          menuItems={domainMenuItems}
        />

        {/* Edit popup modal */}
        <AnchorMenu
          anchorEl={anchorElMenu}
          open={isEditMenuOpen}
          onClose={() => setIsEditMenuOpen(false)}
          menuItems={editMenuItems}
        />

        {/* generate playlist popup modal */}
        <AnchorMenu
          anchorEl={anchorElMenu}
          open={isPlaylistMenuOpen}
          onClose={() => setIsPlaylistMenuOpen(false)}
          menuItems={generatePlaylistMenu}
        />

        {/* Edit popup modal */}
        <BulkEditMediaModal
          isOpen={isBulkEditOpen}
          onClose={() => setIsBulkEditOpen(false)}
          videos={selectedVideos}
          bulkEditType={bulkEditType}
          asAdmin={asAdmin}
          query={videoQuery}
        />

        {/* Upload files popup modal */}
        <UploadVideoFilesComponent
          showUploadPopup={showUploadPopup && !uploadInProgress}
          publisherGroup={groupName || publisherGroup}
          query={videoQuery}
          selectedDomain={selectedDomain}
        />
        {/* Add To Playlist popup modal */}
        <AddToPlaylist
          asAdmin={asAdmin}
          anchorEl={anchorEl}
          publisherSlug={publisherSlug}
          onClose={() => setAnchorEl(null)}
          onSubmit={() => setSelectedVideos([])}
          videos={selectedVideos.map((video) => video.token)}
        />
        {/* Tabs content with section navbar */}
        <SectionNavBar
          buttonsConfig={buttonsConfig[selectedTab]}
          tabsConfig={{
            onChange: (e: any, value?: any) => handleSelectedTab(value),
            selectedTab,
            tabs,
          }}
        />
        {renderTabContent()}
      </Box>
    </Box>
  );
}
